// src/pages/Auth/Login.jsx

import {
  useState,
  useEffect,
} from "react";
import {
  Link,
  useNavigate,
} from "react-router-dom";
import { Eye } from "lucide-react";

import { supabase } from "../../supabaseClient";
import {
  resolvePostLoginDestination,
  savePreferredPortalIntent,
} from "../../lib/access/portalAccess";
import {
  captureReferralCodeFromUrl,
  claimStoredReferralCode,
} from "../../lib/referrals/referrals";

const penguinUrl =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Untitled%20folder/MVP%204/Landing/gwin-penguin.png";

const logoUrl =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png";

function isRunningStandalone() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    window.navigator.standalone === true ||
      window.matchMedia?.(
        "(display-mode: standalone)"
      )?.matches === true ||
      document.referrer?.startsWith(
        "android-app://"
      )
  );
}

function getAuthRedirect() {
  const host =
    window.location.hostname;

  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1";

  if (isLocal) {
    return "http://localhost:3000/auth/callback";
  }

  return (
    process.env
      .REACT_APP_AUTH_CALLBACK_URL ||
    `${window.location.origin}/auth/callback`
  );
}

async function claimReferralIfLearner() {
  try {
    await claimStoredReferralCode(
      supabase
    );
  } catch (error) {
    console.warn(
      "Referral claim skipped:",
      error
    );
  }
}

function GoogleIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />

      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />

      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />

      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    rememberMe,
    setRememberMe,
  ] = useState(false);

  const [error, setError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [
    pwTouched,
    setPwTouched,
  ] = useState(false);

  const navigate = useNavigate();

  const standalone =
    isRunningStandalone();

  useEffect(() => {
    let alive = true;

    captureReferralCodeFromUrl();

    async function routeExistingUser() {
      try {
        const {
          data: { session },
          error: sessionError,
        } =
          await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        const user =
          session?.user || null;

        if (
          !user?.id ||
          !alive
        ) {
          return;
        }

        const result =
          await resolvePostLoginDestination({
            userId: user.id,
            preferredPortal:
              "learner",
            beforeLearnerResolve:
              claimReferralIfLearner,
          });

        if (!alive) return;

        navigate(
          result?.destination ||
            "/login",
          {
            replace: true,
          }
        );
      } catch (routeError) {
        console.error(
          "Existing user routing failed:",
          routeError
        );

        if (alive) {
          setError(
            routeError?.message ||
              "We could not verify your account access."
          );
        }
      }
    }

    routeExistingUser();

    return () => {
      alive = false;
    };
  }, [navigate]);

  

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    setError("");
    setLoading(true);

    try {
      const cleanEmail = email
        .trim()
        .toLowerCase();

      if (
        !cleanEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          cleanEmail
        )
      ) {
        throw new Error(
          "Please enter a valid email"
        );
      }

      if (
        !password ||
        password.length < 6
      ) {
        throw new Error(
          "Password must be at least 6 characters"
        );
      }

      const {
        data,
        error: signInError,
      } =
        await supabase.auth.signInWithPassword(
          {
            email: cleanEmail,
            password,
          }
        );

      if (signInError) {
        throw signInError;
      }

      const user =
        data?.user;

      if (!user?.id) {
        throw new Error(
          "The account was signed in, but the user could not be loaded."
        );
      }

      const result =
        await resolvePostLoginDestination(
          {
            userId: user.id,
            preferredPortal:
              "learner",
            beforeLearnerResolve:
              claimReferralIfLearner,
          }
        );

      navigate(
        result?.destination ||
          "/login",
        {
          replace: true,
        }
      );
    } catch (loginError) {
      setError(
        loginError?.message ||
          "Unable to login."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin =
    async () => {
      try {
        setError("");
        setLoading(true);

        captureReferralCodeFromUrl();

        const redirectTo =
          getAuthRedirect();

        savePreferredPortalIntent(
          "learner"
        );

        const {
          error: oauthError,
        } =
          await supabase.auth.signInWithOAuth(
            {
              provider: "google",
              options: {
                redirectTo,
                queryParams: {
                  prompt:
                    "select_account",
                  access_type:
                    "offline",
                },
              },
            }
          );

        if (oauthError) {
          setError(
            oauthError.message
          );

          setLoading(false);
        }
      } catch (oauthError) {
        setError(
          oauthError?.message ||
            oauthError
              ?.error_description ||
            "Unable to start Google login."
        );

        setLoading(false);
      }
    };

  function getPasswordIssues(
    pwd = ""
  ) {
    const issues = [];

    if (pwd.length < 8) {
      issues.push(
        "at least 8 characters"
      );
    }

    if (!/[a-z]/.test(pwd)) {
      issues.push(
        "a lowercase letter"
      );
    }

    if (!/[A-Z]/.test(pwd)) {
      issues.push(
        "an uppercase letter"
      );
    }

    if (!/[0-9]/.test(pwd)) {
      issues.push("a number");
    }

    if (
      !/[^A-Za-z0-9]/.test(pwd)
    ) {
      issues.push(
        "a special character"
      );
    }

    return issues;
  }

  const missing =
    getPasswordIssues(password);

  return (
    <div className="flex min-h-dvh flex-col overflow-x-hidden bg-white text-[#111111]">
      {/* HEADER */}
      <header
        className="
          sticky
          top-0
          z-50
          w-full
          border-b
          border-[#EFEFEF]
          bg-white
        "
        style={{
          paddingTop:
            "env(safe-area-inset-top, 0px)",
        }}
      >
        <div
          className="
            mx-auto
            flex
            w-full
            max-w-5xl
            items-center
            justify-between
            gap-2
            px-3
            py-3

            min-[375px]:gap-3
            min-[375px]:px-4

            sm:gap-4
            sm:px-6
            sm:py-4
          "
        >
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center"
            aria-label="Gwin home"
          >
            <img
              src={logoUrl}
              alt="Gwin"
              className="
                h-10
                w-auto
                object-contain

                min-[375px]:h-11

                sm:h-12
                md:h-14
              "
              draggable="false"
            />
          </Link>

          <Link
            to="/contact"
            className="
              flex
              min-h-[40px]
              items-center
              justify-center
              rounded-full
              border
              border-[#111111]
              px-3
              py-2
              text-center
              text-[10px]
              font-medium
              leading-tight

              min-[375px]:px-4
              min-[375px]:text-xs

              sm:px-6
              sm:text-sm
            "
          >
            Contact &amp; Legal
            Notices
          </Link>
        </div>
      </header>

      <main className="relative w-full flex-1">
        <div
          className="
            relative
            mx-auto
            flex
            w-full
            max-w-5xl
            flex-col
            px-4
            pb-14

            min-[375px]:px-5
            sm:px-6
          "
        >
                    {/* HERO AREA */}
          <section
            className="
              relative
              z-10
              mx-auto
              w-full
              max-w-[520px]
              px-5
              pt-10
              text-center

              min-[375px]:pt-12

              sm:max-w-[560px]
              sm:px-8
              sm:pt-16

              md:max-w-[620px]
              md:pt-20

              lg:max-w-[680px]
              lg:pt-24
            "
          >
            {/* TOP PENGUIN */}
            <img
              src={penguinUrl}
              alt=""
              aria-hidden="true"
              className="
                pointer-events-none
                absolute
                z-0

                right-[-1%]
                top-[18%]

                w-[41%]

                translate-x-[48%]

                rotate-[-58deg]
                opacity-55
                select-none
              "
              loading="eager"
              decoding="async"
              draggable="false"
            />

            {/* HERO TITLE */}
            <h1
              className="
                relative
                z-10
                mx-auto
                max-w-[460px]

                text-[clamp(1.45rem,6.5vw,2.7rem)]
                font-extrabold
                leading-[1.14]
                tracking-tight

                sm:max-w-[520px]
                md:max-w-[560px]
              "
            >
              You can{" "}
              <span className="text-[#FF914D]">
                build.
              </span>

              <br />

              You can{" "}
              <span className="text-[#FF914D]">
                sell.
              </span>

              <br />

              You can{" "}
              <span className="text-[#FF914D]">
                deliver.
              </span>
            </h1>

            {/* HERO SUBTITLE */}
            <p
              className="
                relative
                z-10
                mx-auto
                mt-6
                max-w-[290px]

                text-[clamp(1.05rem,4.6vw,1.75rem)]
                font-extrabold
                leading-[1.25]
                tracking-tight

                sm:mt-8
                sm:max-w-[330px]

                md:max-w-[360px]
              "
            >
              Now let&apos;s{" "}
              <span className="text-[#FF914D]">
                master finance
              </span>{" "}
              in 5-minutes a day.
            </p>
          </section>
            

          {/* LOGIN CARD */}
          <div
            className="
              relative
              z-20
              mx-auto
              mb-16
              mt-9
              w-[76%]

              sm:mt-12
              sm:w-[68%]

              md:w-[60%]

              lg:w-[52%]

              xl:w-[46%]
            "
          >
            <img
  src={penguinUrl}
  alt=""
  aria-hidden="true"
  className="
    pointer-events-none
    absolute
    z-0

    left-[-18%]
    top-[100%]

    w-[55%]

    -translate-x-[48%]
    -translate-y-1/2

    rotate-[62deg]
    opacity-55
    select-none
  "
  loading="eager"
  decoding="async"
  draggable="false"
/>

            <section
              className="
                relative
                z-10
                w-full
                rounded-[22px]
                border
                border-[#DEDEDE]
                bg-white
                p-5
                shadow-[0_2px_8px_rgba(0,0,0,0.03)]

                min-[375px]:p-6

                sm:rounded-[28px]
                sm:p-7
              "
            >
              

              <form
                className="space-y-5"
                onSubmit={
                  handleSubmit
                }
              >
                {error && (
                  <div
                    role="alert"
                    className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-semibold leading-relaxed text-red-700 min-[375px]:text-sm"
                  >
                    {error}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="login-email"
                    className="mb-2 block text-sm font-extrabold"
                  >
                    Email
                  </label>

                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(
                      event
                    ) =>
                      setEmail(
                        event.target
                          .value
                      )
                    }
                    placeholder="Enter your email"
                    className="
                      min-h-[48px]
                      w-full
                      rounded-xl
                      border
                      border-[#D8D8D8]
                      bg-white
                      px-4
                      py-3
                      text-sm
                      outline-none

                      focus:border-[#FF914D]
                      focus:ring-2
                      focus:ring-[#FF914D]/15
                    "
                    disabled={loading}
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>

                <div>
                  <label
                    htmlFor="login-password"
                    className="mb-2 block text-sm font-extrabold"
                  >
                    Password
                  </label>

                  <div className="relative">
                    <input
                      id="login-password"
                      type={
                        showPassword
                          ? "text"
                          : "password"
                      }
                      value={password}
                      onChange={(
                        event
                      ) =>
                        setPassword(
                          event.target
                            .value
                        )
                      }
                      onBlur={() =>
                        setPwTouched(
                          true
                        )
                      }
                      placeholder="Enter your password"
                      className="
                        min-h-[48px]
                        w-full
                        rounded-xl
                        border
                        border-[#D8D8D8]
                        bg-white
                        px-4
                        py-3
                        pr-12
                        text-sm
                        outline-none

                        focus:border-[#FF914D]
                        focus:ring-2
                        focus:ring-[#FF914D]/15
                      "
                      disabled={
                        loading
                      }
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword(
                          !showPassword
                        )
                      }
                      className="
                        absolute
                        right-3
                        top-1/2
                        -translate-y-1/2
                        rounded-lg
                        p-1.5
                      "
                      aria-label={
                        showPassword
                          ? "Hide password"
                          : "Show password"
                      }
                    >
                      <Eye className="h-4 w-4 text-[#757575]" />
                    </button>
                  </div>

                  {pwTouched &&
                    password &&
                    missing.length >
                      0 && (
                      <div
                        role="status"
                        aria-live="polite"
                        className="mt-2 rounded-lg bg-[#FFF6EF] p-3 text-[11px] font-medium leading-relaxed text-[#6B5A4C]"
                      >
                        This password
                        is missing:{" "}
                        <strong>
                          {missing.join(
                            ", "
                          )}
                          .
                        </strong>
                      </div>
                    )}
                </div>

                <div className="flex flex-col gap-3 text-xs min-[390px]:flex-row min-[390px]:items-center min-[390px]:justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-[#757575]">
                    <input
                      type="checkbox"
                      checked={
                        rememberMe
                      }
                      onChange={(
                        event
                      ) =>
                        setRememberMe(
                          event.target
                            .checked
                        )
                      }
                      className="h-4 w-4"
                    />

                    <span>
                      Stay logged in?
                    </span>
                  </label>

                  <Link
                    to="/forgot-password"
                    className="font-semibold text-[#FF914D]"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="
                    flex
                    min-h-[52px]
                    w-full
                    items-center
                    justify-center
                    rounded-2xl
                    bg-[#FF914D]
                    px-5
                    py-3
                    text-sm
                    font-extrabold
                    text-white
                    transition

                    hover:bg-[#E97D3B]

                    disabled:cursor-not-allowed
                    disabled:opacity-50
                  "
                >
                  {loading
                    ? "Logging in..."
                    : "Login"}
                </button>

                <p className="text-center text-xs font-medium leading-relaxed min-[375px]:text-sm">
                  Don&apos;t have
                  an account yet?{" "}
                  <Link
                    to="/register"
                    replace
                    className="font-extrabold text-[#FF914D]"
                  >
                    Register here
                  </Link>
                </p>

                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-[#E3E3E3]" />

                  <span className="text-[11px] font-semibold text-[#757575]">
                    Or login with
                  </span>

                  <div className="h-px flex-1 bg-[#E3E3E3]" />
                </div>

                <button
                  type="button"
                  onClick={
                    handleGoogleLogin
                  }
                  disabled={loading}
                  aria-disabled={
                    loading
                  }
                  className="
                    flex
                    min-h-[50px]
                    w-full
                    items-center
                    justify-center
                    gap-3
                    rounded-2xl
                    border
                    border-[#D8D8D8]
                    bg-white
                    px-4
                    py-3
                    text-sm
                    font-bold
                    transition

                    hover:bg-[#F8F8F8]

                    disabled:opacity-50
                  "
                >
                  <GoogleIcon />
                  Google
                </button>
              </form>
            </section>
          </div>

          
        </div>
      </main>
    </div>
  );
}